import {
  BlotterTable,
  Box,
  useBlotterTable,
  useConstant,
  useSecuritiesContext,
  type Column,
  type Order,
} from '@talos/kyoko';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { memo, useMemo } from 'react';
import { of } from 'rxjs';
import {
  MarketDistributionAveragePriceColumn,
  MarketDistributionAvgPxAllInColumn,
  MarketDistributionMarketColumn,
} from '../columns';
import { DistributionWrapper } from '../styles';
import { REMAINDER } from '../types';
import { useChartOptions } from '../useChartOptions';
import { useMarketDistribution } from '../useMarketDistribution';

export interface FilledMarketDistributionChartProps {
  legIndex?: 0 | 1;
  order: Order;
}

export const FilledMarketDistributionChart = memo(function FilledMarketDistributionChart({
  legIndex,
  order,
}: FilledMarketDistributionChartProps) {
  const { distribution } = useMarketDistribution({
    order: order,
    legIndex,
    includeZeroCumQty: false,
    variant: 'Market',
  });

  const highchartOptions = useChartOptions({ distribution, centerText: 'Market Distribution' });
  const remainderFreeDistribution = useMemo(() => distribution.filter(d => d.id !== REMAINDER), [distribution]);

  const { securitiesBySymbol } = useSecuritiesContext();
  const currentSymbol =
    (legIndex != null && securitiesBySymbol.get(order.Symbol)?.MultilegDetails?.Legs[legIndex].Symbol) || order.Symbol;

  const distributionObs = useMemo(
    () => of({ initial: true, data: remainderFreeDistribution }),
    [remainderFreeDistribution]
  );

  const columns = useConstant([
    MarketDistributionMarketColumn,
    {
      type: 'price',
      field: 'cumQty',
      title: 'Qty',
      params: {
        quoteCurrencyField: 'sizeCurrency',
        showDashIfZero: true,
      },
    },
    {
      type: 'percent',
      field: 'y',
      title: 'Pct',
      initialSort: '-',
      params: {
        increment: '0.01',
      },
    },
    MarketDistributionAveragePriceColumn({ tab: 'filled' }),
    {
      type: 'price',
      field: 'cumFee',
      title: 'Fees',
      width: 100,
      params: {
        asset: currentSymbol,
        showDashIfZero: false,
      },
    },
    MarketDistributionAvgPxAllInColumn,
  ] satisfies Column[]);

  const blotterTable = useBlotterTable({
    dataObservable: distributionObs,
    errorObservable: undefined,
    columns,
    rowID: 'id',
    gridOptions: {
      domLayout: 'autoHeight',
      onFirstDataRendered: ({ api }) => {
        // Only auto-size columns with synchronously rendered content.
        // Fees and Avg Px All In use InlineFormattedNumber which renders asynchronously,
        // so their widths are set explicitly above to avoid measuring empty cells.
        api.autoSizeColumns(['market', 'cumQty', 'y', 'avgPx']);
      },
    },
  });

  if (highchartOptions == null) {
    return null;
  }

  return (
    <DistributionWrapper>
      <Box h="250px">
        <HighchartsReact highcharts={Highcharts} options={highchartOptions} />
      </Box>

      <Box minHeight="80px" w="100%">
        <BlotterTable background="gray.030" {...blotterTable} />
      </Box>
    </DistributionWrapper>
  );
});
